import { Access } from "@p0security/shared/types/access/index";

import { LAUNCHDARKLY_BASE_ROLES, LAUNCHDARKLY_MEMBER_ROLES } from "./roles.ts";

// The access grants on the requester's existing LaunchDarkly member, found
// by their P0 email, in one installed account. Its one required parameter
// names the account and the target together, as `<account>/<target>`, so a
// request reads `base-role acme-prod/writer`. With one account installed, a
// bare target names it.
//
// Normalize resolves the account and LaunchDarkly's ids and sets them as the
// generated parameters below. Policies, the lock and request-time checks see
// only the permission, so the ids are parameters, not generated data. Stage
// resolves them again and refuses on a change. `generated` holds only what
// stage records: the member's prior state.

// The account's install item id. Not a locator for `contextFromLocator`,
// whose `${locator}Id` convention would read `accountId` below.
const account = {
  description: "The LaunchDarkly account, by its name in P0",
  label: "Account",
  type: "generated",
} as const;

const accountId = {
  description: "The LaunchDarkly account's own ID",
  label: "LaunchDarkly account ID",
  type: "generated",
} as const;

const memberId = {
  description: "Your LaunchDarkly member, found by your email",
  label: "LaunchDarkly member ID",
  type: "generated",
} as const;

export const LaunchDarklyAccesses = {
  "base-role": Access({
    delegation: {},
    description:
      "A LaunchDarkly base role (Reader, Writer or Admin) on your existing LaunchDarkly member",
    generated: {
      // The member's base role when the request was staged, which can be any
      // role LaunchDarkly reports. Required: a revoke lowers the member to
      // the highest role still granted, or to this one when none is.
      priorRole: {
        choices: LAUNCHDARKLY_MEMBER_ROLES,
        description: "The base role restored when this access ends",
        label: "Prior base role",
        type: "string",
      },
    },
    label: "Base role",
    maturity: "preview",
    // Reads `base-role <role>`, as in `base-role acme-prod/writer`.
    parameters: {
      // The description says `{account}/{role}` because Slack shows `<` and
      // `>` in a placeholder as `&lt;` and `&gt;`.
      role: {
        description: "LaunchDarkly account and base role, as {account}/{role}",
        label: "Role",
        type: "static",
      },
      account,
      accountId,
      memberId,
      baseRole: {
        choices: LAUNCHDARKLY_BASE_ROLES,
        label: "Base role",
        type: "generated",
      },
    },
    render: { locator: "account", policy: "baseRole" } as const,
  }),
};

export type LaunchDarklyAccesses = typeof LaunchDarklyAccesses;
export type LaunchDarklyBaseRoleAccess = LaunchDarklyAccesses["base-role"];
